import React, { useEffect } from 'react';

/** Full-screen image viewer with prev/next arrows + counter. Rendered over
 *  everything (fixed overlay) so it sits above whatever modal opened it. */
export const ImageLightbox: React.FC<{
  images: string[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
}> = ({ images, index, onIndexChange, onClose }) => {
  const prev = () => onIndexChange((index - 1 + images.length) % images.length);
  const next = () => onIndexChange((index + 1) % images.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, images.length]);

  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 44,
    height: 44,
    borderRadius: 'var(--radius-full)',
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    fontSize: 24,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.9)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <button
        onClick={onClose}
        style={{ ...arrowStyle, top: 24, right: 24, transform: 'none', fontSize: 22 }}
      >
        ×
      </button>
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} style={{ ...arrowStyle, left: 24 }}>
            ‹
          </button>
          <button onClick={(e) => { e.stopPropagation(); next(); }} style={{ ...arrowStyle, right: 24 }}>
            ›
          </button>
          <div
            style={{
              position: 'absolute',
              top: 28,
              left: 24,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              background: 'rgba(0,0,0,0.55)',
              borderRadius: 'var(--radius-full)',
              padding: '4px 12px',
            }}
          >
            {index + 1} / {images.length}
          </div>
        </>
      )}
      <img
        src={images[index]}
        /*
         * A screen reader already announces this as an image, so the word
         * itself said nothing — "image 2 of 3" read aloud as "image, image 2
         * of 3". What is actually useful is the position, which is the only
         * thing this component knows: the pages that open it pass URLs, not
         * descriptions.
         */
        alt={
          images.length > 1
            ? `Attachment ${index + 1} of ${images.length}`
            : 'Attachment'
        }
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
      />
    </div>
  );
};
